/**
 * Web sibling of AIChat — picked by the web bundler ahead of the mobile
 * file; Metro never sees it.
 *
 * The phone opens Orion as a full-screen modal. On the web the modal filled
 * the browser window edge to edge, which is wrong at desktop width. Here
 * Orion is a docked panel: bottom-right, above its button, 400px wide, with
 * its own scrolling message list (a chat panel scrolls inside itself; the
 * page behind it stays where it was). Below 1024px it takes the whole
 * window, as on the phone. Same header, bubbles, input and copy; Enter
 * sends and Shift+Enter adds a line; Escape closes.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Image,
} from 'react-native';

import { useAI } from '../../contexts/AIContext';
import * as NavigationService from '../../services/navigationService';
import { ms, s } from '../../utils/responsive';

import { AIMessageBubble } from './AIMessageBubble';
import { ErrorBoundary } from './ErrorBoundary';

const DESKTOP_MIN_WIDTH = 1024;
const PANEL_WIDTH = 400;

function readWidth(): number {
  const g = globalThis as unknown as { innerWidth?: number };
  return g.innerWidth ?? 0;
}

export const AIChat: React.FC = () => {
  const { messages, isLoading, isChatOpen, sendMessage, closeChat, error, clearError } = useAI();
  const [inputText, setInputText] = useState('');
  const [width, setWidth] = useState(readWidth);
  // The app's tsconfig has no DOM lib; the ref only needs these two fields.
  const listRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);

  useEffect(() => {
    const g = globalThis as unknown as {
      addEventListener?: (t: string, h: (e: any) => void) => void;
      removeEventListener?: (t: string, h: (e: any) => void) => void;
    };
    const onResize = () => setWidth(readWidth());
    const onKey = (e: { key?: string }) => {
      if (e.key === 'Escape') closeChat();
    };
    g.addEventListener?.('resize', onResize);
    if (isChatOpen) g.addEventListener?.('keydown', onKey);
    return () => {
      g.removeEventListener?.('resize', onResize);
      g.removeEventListener?.('keydown', onKey);
    };
  }, [isChatOpen, closeChat]);

  // Keep the newest message in view.
  useEffect(() => {
    const el = listRef.current;
    if (el) {
      setTimeout(() => {
        el.scrollTop = el.scrollHeight;
      }, 50);
    }
  }, [messages, isLoading]);

  const handleNavigate = (screen: string, params?: any) => {
    closeChat();
    // @ts-ignore - Type safety is handled by the service
    NavigationService.navigate(screen, params);
  };

  const handleSend = () => {
    if (inputText.trim() && !isLoading) {
      sendMessage(inputText);
      setInputText('');
    }
  };

  if (!isChatOpen) return null;

  const docked = width >= DESKTOP_MIN_WIDTH;

  return (
    <View style={[styles.layer, docked ? styles.layerDocked : styles.layerFull]} pointerEvents="box-none">
      <View style={[styles.panel, docked ? styles.panelDocked : styles.panelFull]} accessibilityRole="none">
        <ErrorBoundary>
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View style={styles.avatarSmall}>
                <Image
                  source={require('../../assets/icons/chat.png')}
                  style={styles.avatarImage}
                  resizeMode="contain"
                />
              </View>
              <View>
                <Text style={styles.headerTitle}>Orion</Text>
                <Text style={styles.headerSubtitle}>Your AI Planning Assistant</Text>
              </View>
            </View>
            <TouchableOpacity
              onPress={closeChat}
              style={styles.closeButton}
              accessibilityRole="button"
              accessibilityLabel="Close Orion"
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          {error && (
            <View style={styles.errorBanner}>
              <Text style={styles.errorText}>{error}</Text>
              <TouchableOpacity onPress={clearError} accessibilityRole="button" accessibilityLabel="Dismiss">
                <Text style={styles.errorClose}>✕</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* A real scrolling region: the app's ScrollView is page-scroll on web. */}
          {React.createElement(
            'div',
            { ref: listRef, style: listStyle },
            <View style={styles.messagesContent}>
              {messages.length === 0 && !isLoading && (
                <Text style={styles.emptyText}>
                  Ask about anything in your vault, or where to start.
                </Text>
              )}
              {messages.map((message, index) => {
                if (!message || typeof message !== 'object') return null;
                if (!message.content || typeof message.content !== 'string') return null;
                if (message.content.trim() === '') return null;
                return (
                  <ErrorBoundary key={`message-${index}-${message.timestamp || index}`}>
                    <AIMessageBubble
                      message={message}
                      onNavigate={handleNavigate}
                      onSendMessage={sendMessage}
                    />
                  </ErrorBoundary>
                );
              })}
              {isLoading && (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="small" color="#29BFC0" />
                  <Text style={styles.loadingText}>Orion is thinking...</Text>
                </View>
              )}
            </View>,
          )}

          <View style={styles.inputContainer}>
            <View style={styles.inputWrapper}>
              <TextInput
                style={styles.input}
                value={inputText}
                onChangeText={(text) => setInputText(typeof text === 'string' ? text : '')}
                onKeyPress={(e) => {
                  const native = e.nativeEvent as { key?: string; shiftKey?: boolean };
                  if (native.key === 'Enter' && !native.shiftKey) {
                    (e as unknown as { preventDefault?: () => void }).preventDefault?.();
                    handleSend();
                  }
                }}
                placeholder="Type your message..."
                placeholderTextColor="#999"
                multiline
                maxLength={500}
                editable={!isLoading}
                autoCorrect
                autoCapitalize="sentences"
                accessibilityLabel="Message Orion"
              />
              <TouchableOpacity
                style={[styles.sendButton, (!inputText.trim() || isLoading) && styles.sendButtonDisabled]}
                onPress={handleSend}
                disabled={!inputText.trim() || isLoading}
                accessibilityRole="button"
                accessibilityLabel="Send"
              >
                <Text style={styles.sendButtonText}>➤</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.inputHint}>
              Orion can help you organize your end-of-life planning · Enter to send
            </Text>
          </View>
        </ErrorBoundary>
      </View>
    </View>
  );
};

const listStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  backgroundColor: '#FFFFFF',
};

const styles = StyleSheet.create({
  layer: {
    // `fixed` is valid in react-native-web; the RN types only list the
    // native values, hence the casts.
    position: 'fixed' as unknown as 'absolute',
    zIndex: 200,
  },
  layerDocked: {
    right: 24,
    bottom: 96,
  },
  layerFull: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  panel: {
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    flexDirection: 'column',
  },
  panelDocked: {
    width: PANEL_WIDTH,
    height: 'min(640px, calc(100vh - 120px))' as unknown as number,
    borderRadius: ms(16),
    shadowColor: '#1C2C4F',
    shadowOpacity: 0.22,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
    borderWidth: 1,
    borderColor: '#E9EEF1',
  },
  panelFull: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    backgroundColor: '#29BFC0',
    borderBottomWidth: ms(1),
    borderBottomColor: 'rgba(255, 255, 255, 0.2)',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarSmall: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: ms(12),
  },
  avatarImage: {
    width: s(30),
    height: s(30),
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  headerSubtitle: {
    fontSize: ms(12, 0.3),
    color: 'rgba(255, 255, 255, 0.8)',
  },
  closeButton: {
    width: s(36),
    height: s(36),
    borderRadius: ms(18),
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: ms(24, 0.3),
    color: '#FFFFFF',
    fontWeight: '300',
  },
  errorBanner: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#FFE5E5',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#FFCCCC',
  },
  errorText: {
    flex: 1,
    fontSize: ms(14, 0.3),
    color: '#CC0000',
  },
  errorClose: {
    fontSize: ms(20, 0.3),
    color: '#CC0000',
    paddingLeft: ms(12),
  },
  messagesContent: {
    paddingVertical: ms(16),
  },
  emptyText: {
    fontSize: ms(14, 0.3),
    lineHeight: ms(20, 0.3),
    color: '#596B85',
    textAlign: 'center',
    paddingHorizontal: ms(24),
    paddingVertical: ms(24),
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: ms(12),
  },
  loadingText: {
    marginLeft: ms(8),
    fontSize: ms(14, 0.3),
    color: '#666',
    fontStyle: 'italic',
  },
  inputContainer: {
    paddingHorizontal: ms(12),
    paddingVertical: ms(8),
    backgroundColor: '#F8F8F8',
    borderTopWidth: ms(1),
    borderTopColor: '#E0E0E0',
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(24),
    paddingHorizontal: ms(16),
    paddingVertical: ms(8),
    borderWidth: ms(1),
    borderColor: '#E0E0E0',
  },
  input: {
    flex: 1,
    fontSize: ms(16, 0.3),
    maxHeight: s(100),
    paddingVertical: ms(4),
    color: '#333',
  },
  sendButton: {
    width: s(36),
    height: s(36),
    borderRadius: ms(18),
    backgroundColor: '#29BFC0',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: ms(8),
  },
  sendButtonDisabled: {
    backgroundColor: '#CCCCCC',
  },
  sendButtonText: {
    fontSize: ms(20, 0.3),
    color: '#FFFFFF',
  },
  inputHint: {
    fontSize: ms(11, 0.3),
    color: '#888',
    marginTop: ms(4),
    textAlign: 'center',
  },
});

export default AIChat;
